import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CalendarHeart, ArrowLeft, Check, Users, Camera, MessageCircle, AlertTriangle, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import PublicNav from "../components/PublicNav";
import Seo from "../components/Seo";
import PublicFooter from "../components/PublicFooter";
import { getVendorBySlug } from "../lib/vendors";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function ProductDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve back-button target from ?from= param
  const back = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const from = sp.get("from") || "";
    if (from === "dwg") {
      return { label: "Kembali ke Paket Wedding DWG", to: "/paket-wedding-dwg", testid: "product-back-dwg" };
    }
    if (from.startsWith("vendor:")) {
      const slug = from.split(":")[1];
      const v = getVendorBySlug(slug);
      return {
        label: `Kembali ke ${v ? v.name : "Vendor"}`,
        to: `/vendor/${slug}`,
        testid: "product-back-vendor",
      };
    }
    return { label: "Kembali ke Beranda", to: "/", testid: "product-back-home" };
  }, []);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/public/catalog/${pid}`)
      .then(({ data }) => {
        setP(data);
        return publicApi.get(`/public/catalog?family=${encodeURIComponent(data.product_family)}`);
      })
      .then(({ data }) => setRelated((data || []).filter(x => x.product_id !== pid)))
      .catch(() => toast.error("Produk tidak ditemukan"))
      .finally(() => setLoading(false));
  }, [pid]);

  if (loading) return (
    <div className="min-h-screen bg-[#F7F1E8]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PublicNav />
      <div className="flex items-center justify-center py-32 text-[#8B5A2A] font-serif italic">Memuat...</div>
    </div>
  );
  if (!p) return (
    <div className="min-h-screen bg-[#F7F1E8]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PublicNav />
      <div className="flex items-center justify-center py-32 text-[#8B5A2A]">Produk tidak tersedia.</div>
    </div>
  );

  const hasPromo = p.promo_active && p.promo_price > 0;
  const currentPrice = p.current_price ?? (hasPromo ? p.promo_price : p.normal_price);
  const heroImage = resolveImg(p.detail_image) || resolveImg(p.card_image) ||
    "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80";

  const startConsult = () => nav(`/consultation?product_id=${p.product_id}`);

  return (
    <div className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="product-detail-page" data-public-page="true">
      <style>{`.font-serif{font-family:'Cormorant Garamond',serif} .gold-line{background:linear-gradient(90deg,transparent 0%,#C4A57B 20%,#C4A57B 80%,transparent 100%);height:1px} .luxury-shadow{box-shadow:0 20px 60px -20px rgba(139, 90, 42, 0.35)}`}</style>
      <Seo title={p.product_name} path={`/catalog/${p.product_id}`} image={heroImage}
        description={(p.short_description || `${p.product_name} dari Dessy Wedding Group — harga mulai Rp ${Math.round(currentPrice).toLocaleString("id-ID")}. Konsultasi gratis.`).slice(0, 160)}
        jsonLd={{ "@context": "https://schema.org", "@type": "Product", name: p.product_name, image: heroImage,
          description: p.short_description || p.product_name, brand: { "@type": "Brand", name: "Dessy Wedding Group" },
          offers: { "@type": "Offer", priceCurrency: "IDR", price: Math.round(currentPrice), availability: "https://schema.org/InStock",
            url: `https://dessywedding.site/catalog/${p.product_id}` } }} />
      <PublicNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={() => nav(back.to)}
          className="inline-flex items-center gap-1.5 text-xs text-[#5C4A3D] hover:text-[#8B5A2A] font-medium"
          data-testid={back.testid}
        >
          <ArrowLeft className="w-3.5 h-3.5"/> {back.label}
        </button>
      </div>

      <section className="relative h-[420px] overflow-hidden">
        <img src={heroImage} alt={p.product_name} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1611] via-[#1E1611]/50 to-transparent"/>
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 pb-10 text-white">
          {p.event_category && <div className="text-[10px] uppercase tracking-[0.3em] text-[#C4A57B] font-semibold mb-2">{p.event_category} · {p.product_family}</div>}
          <h1 className="font-serif text-4xl md:text-6xl font-medium leading-tight">{p.product_name}</h1>
          {p.brand && <div className="mt-2 text-sm text-[#E5D9C4] italic font-light">{p.brand}</div>}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {p.short_description && (
            <p className="text-[#5C4A3D] text-lg font-light leading-relaxed">{p.short_description}</p>
          )}
          {p.full_description && p.full_description !== p.short_description && (
            <p className="text-[#5C4A3D] text-sm leading-relaxed">{p.full_description}</p>
          )}
          {(p.guest_capacity > 0 || p.crew_count > 0) && (
            <div className="flex gap-6 py-4 border-y border-[#E5D9C4]">
              {p.guest_capacity > 0 && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#8B5A2A]"/>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B5A2A] font-semibold">Kapasitas Tamu</div>
                    <div className="font-serif text-xl">{p.guest_capacity} orang</div>
                  </div>
                </div>
              )}
              {p.crew_count > 0 && (
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-[#8B5A2A]"/>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B5A2A] font-semibold">Crew</div>
                    <div className="font-serif text-xl">{p.crew_count} orang</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {p.included_items?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-4">Yang Sudah Termasuk</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {p.included_items.map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[#3A2E28]">
                    <Check className="w-4 h-4 text-[#8B5A2A] shrink-0 mt-0.5"/>
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.important_notes?.length > 0 && p.important_notes.some(n => n) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-800 font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5"/> Catatan Penting</div>
              <ul className="space-y-1.5 text-sm text-amber-900">
                {p.important_notes.filter(n => n).map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
          )}

          {p.terms_and_conditions?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-4 flex items-center gap-2"><FileText className="w-3.5 h-3.5"/> Terms & Conditions</div>
              <ul className="space-y-2 text-sm text-[#5C4A3D]">
                {p.terms_and_conditions.map((t, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#8B5A2A] font-mono">{String(i+1).padStart(2,"0")}</span>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="sticky top-24 bg-white border border-[#E5D9C4] rounded-2xl p-6 luxury-shadow" data-testid="detail-price-card">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold">{hasPromo ? "Promo" : "Investasi"}</div>
            {hasPromo ? (
              <>
                <div className="text-sm text-[#8C7A5C] line-through font-mono mt-1">{formatIDR(p.normal_price)}</div>
                <div className="font-serif font-medium text-rose-700 text-4xl">{formatIDR(p.promo_price)}</div>
                <div className="text-[10px] text-rose-700 uppercase tracking-widest font-semibold mt-1">
                  Hemat {formatIDR(p.normal_price - p.promo_price)}
                </div>
                {p.promo_end_date && <div className="text-[10px] text-[#8B5A2A] mt-2">Promo berakhir: {p.promo_end_date}</div>}
              </>
            ) : (
              <div className="font-serif font-medium text-[#1E1611] text-4xl">{formatIDR(currentPrice)}</div>
            )}
            <div className="gold-line my-5"/>
            <Button onClick={startConsult} className="w-full bg-[#1E1611] text-white hover:bg-[#8B5A2A] rounded-full py-6 text-xs uppercase tracking-widest font-semibold" data-testid="btn-consult-package">
              <CalendarHeart className="w-4 h-4 mr-2"/> Konsultasikan Paket Ini
            </Button>
            <div className="text-[10px] text-[#8B5A2A] text-center mt-3 uppercase tracking-widest">Atau chat langsung</div>
            <a href={`https://wa.me/628112225912?text=${encodeURIComponent(`Halo Dessy Wedding, saya tertarik dengan paket ${p.product_name} (${formatIDR(currentPrice)}). Bisa info lebih lanjut?`)}`}
              target="_blank" rel="noreferrer"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-[#8B5A2A] text-[#8B5A2A] px-4 py-3 rounded-full font-semibold hover:bg-[#8B5A2A] hover:text-white text-xs uppercase tracking-widest">
              <MessageCircle className="w-4 h-4"/> WhatsApp
            </a>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold mb-4">Paket Lain di Family Sama</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.slice(0, 3).map(r => {
              const sp = new URLSearchParams(window.location.search);
              const from = sp.get("from");
              const suffix = from ? `?from=${from}` : "";
              return (
                <Link key={r.product_id} to={`/catalog/${r.product_id}${suffix}`}
                  className="bg-white rounded-xl overflow-hidden border border-[#E5D9C4] hover:-translate-y-0.5 transition-transform block" data-testid={`related-${r.product_id}`}>
                  <div className="p-4">
                    <div className="text-[9px] uppercase tracking-widest text-[#8B5A2A] font-semibold">{r.event_category}</div>
                    <div className="font-serif text-lg mt-1">{r.product_name}</div>
                    <div className="font-mono text-sm mt-2 text-[#1E1611]">{formatIDR(r.promo_active ? r.promo_price : r.normal_price)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
      <PublicFooter />
    </div>
  );
}
